<template>
  <div class="product-feeds-view">
    <PageHeader
      :title="$t('products.product_feeds')"
      :description="$t('products.product_feeds_description')"
    >
      <template #actions>
        <Btn @click="handleAddFeed">{{ $t('products.add_feed') }}</Btn>
      </template>
    </PageHeader>
    <div class="table-container">
      <div class="table-heading">
        <div class="table-heading__title">
          {{ $t('products.product_feeds_overview') }}
          <span class="table-heading__count">{{ feeds.length }}</span>
        </div>
      </div>
      <EasyDataTable
        :headers="feedHeaders"
        :items="feeds"
        @click-row="selectFeed"
        table-class-name="custom-table"
      >
        <template #item-name="item">
          <TableRowLink :to="`/products/${item.id}`" :aria-label="item.name">
            {{ item.name }}
          </TableRowLink>
        </template>
        <template #item-actions="item">
          <button @click.stop="bulkRaiseFeedPrices(item)" title="Bulk prijsupdate">
            <BaseIcon name="zap" size="16" />
          </button>
          <button @click.stop="editFeed(item)"><BaseIcon name="edit" size="16" /></button>
        </template>
      </EasyDataTable>
    </div>
  </div>
</template>

<script>
  import { ref, reactive, computed } from 'vue';
  import EasyDataTable from 'vue3-easy-data-table';
  import 'vue3-easy-data-table/dist/style.css';
  import {
    getProductFeedSummaries,
    addProductFeed,
    updateProductFeed,
    deleteProductFeed,
    bulkUpdateProducts,
  } from '@shared/services/productService';
  import PageHeader from '@/components/partials/PageHeader.vue';
  import Btn from '@/components/partials/Btn.vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';
  import TableRowLink from '@/components/tables/TableRowLink.vue';
  import { useRouter } from 'vue-router';
  import { useUserStore } from '@/store/userStore';
  import { useUIStore } from '@/store/uiStore';
  import { useToastStore } from '@/store/toastStore';
  import FeedSetupModal from '@/components/products/FeedSetupModal.vue';
  import BulkPriceUpdateModal from '@/components/products/BulkPriceUpdateModal.vue';

  export default {
    name: 'ProductFeedsView',
    components: { EasyDataTable, PageHeader, Btn, BaseIcon, TableRowLink },
    setup() {
      const router = useRouter();
      const userStore = useUserStore();
      const uiStore = useUIStore();
      const toastStore = useToastStore();
      const feeds = ref([]);

      const feedHeaders = [
        { text: 'Naam', value: 'name' },
        { text: 'Aantal producten', value: 'productCount' },
        { text: 'Acties', value: 'actions', sortable: false },
      ];

      async function refreshFeeds() {
        try {
          const organizationId = userStore.currentOrganizationId;
          let data;

          if (organizationId) {
            data = await getProductFeedSummaries();
          } else {
            console.warn('No organizationId, using mock data');
            data = await getProductFeedSummaries();
          }

          feeds.value = data.map((feed) => ({
            ...feed,
            productCount: feed.products.length,
          }));
        } catch (error) {
          console.error('Error loading feeds:', error);
        }
      }
      refreshFeeds();

      function selectFeed(feed) {
        // Navigate to feed detail page
        router.push(`/products/${feed.id}`);
      }

      // Handle adding new feed
      const handleAddFeed = () => {
        const modalId = uiStore.addModal(FeedSetupModal, {
          title: 'Feed toevoegen',
          description: 'Maak een nieuwe product feed aan',
          feed: {
            name: '',
            description: '',
            attributes: [],
          },
          isEdit: false,
          onSubmit: async (feedData) => {
            try {
              const organizationId = userStore.currentOrganizationId;

              if (!organizationId) {
                alert('Geen organization ID beschikbaar');
                return;
              }

              await addProductFeed(feedData, organizationId);
              uiStore.closeModal(modalId);
              refreshFeeds();
            } catch (error) {
              console.error('Error creating feed:', error);
              alert('Er is een fout opgetreden bij het aanmaken');
            }
          },
        });
      };
      function editFeed(feed) {
        const modalId = uiStore.addModal(FeedSetupModal, {
          title: 'Feed Bewerken',
          description: `Bewerk de feed "${feed.name}"`,
          feed: {
            id: feed.id,
            name: feed.name,
            description: feed.description || '',
            purpose: feed.purpose || 'sales',
            attributes: feed.attributes || [],
          },
          isEdit: true,
          onSubmit: async (feedData) => {
            try {
              await updateProductFeed(feed.id, feedData);
              uiStore.closeModal(modalId);
              refreshFeeds();
            } catch (error) {
              console.error('Error updating feed:', error);
              alert('Er is een fout opgetreden bij het bijwerken');
            }
          },
          onDeleteFeed: (feedId) => {
            uiStore.closeModal(modalId);
            const confirmModalId = uiStore.addModal(false, {
              title: 'Feed verwijderen?',
              description: 'Weet je zeker dat je deze feed wilt verwijderen? Dit kan niet ongedaan worden gemaakt.',
              submitText: 'Ja, verwijderen',
              cancelText: 'Annuleren',
              status: 'danger',
              onSubmit: async () => {
                try {
                  await deleteProductFeed(feedId);
                  uiStore.closeModal(confirmModalId);
                  toastStore.addToast({ type: 'success', message: 'Feed verwijderd' });
                  refreshFeeds();
                } catch (error) {
                  console.error('Error deleting feed:', error);
                  toastStore.addToast({ type: 'error', message: 'Er is een fout opgetreden bij het verwijderen' });
                }
              },
              onCancel: () => {
                uiStore.closeModal(confirmModalId);
              },
            });
          },
        });
      }
      function bulkRaiseFeedPrices(feed) {
        let isUpdating = false;
        const modalId = uiStore.addModal(BulkPriceUpdateModal, {
          title: 'Prijsupdate',
          size: 'sm',
          description: `Met hoeveel procent wil je alle prijzen in "${feed.name}" aanpassen?`,
          defaultPercentage: 5,
          beforeClose: () => !isUpdating,
          onConfirm: async (percentage) => {
            isUpdating = true;
            uiStore.updateModalProps(modalId, { loading: true });
            try {
              await bulkUpdateProducts({
                mode: 'percentage',
                field: 'price',
                value: percentage,
                filter: { feed_id: feed.id },
              });
              uiStore.closeModal(modalId);
              toastStore.addToast({ type: 'success', message: 'Productprijzen bijgewerkt' });
              await refreshFeeds();
            } catch (error) {
              console.error('Error bulk updating products:', error);
              uiStore.updateModalProps(modalId, { loading: false });
              toastStore.addToast({
                type: 'error',
                message: 'Er is een fout opgetreden bij de bulk-update',
              });
            } finally {
              isUpdating = false;
            }
          },
        });
      }
      return {
        feeds,
        feedHeaders,
        selectFeed,
        handleAddFeed,
        editFeed,
        bulkRaiseFeedPrices,
      };
    },
  };
</script>

<style scoped>
  .product-feeds-view {
    padding: 2rem;
  }

  .modal {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.2);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
  }
  .modal-content {
    background: #fff;
    padding: 2rem;
    border-radius: 8px;
    min-width: 320px;
    max-width: 90vw;
  }
  .modal-actions {
    margin-top: 1rem;
    display: flex;
    gap: 1rem;
  }

  .cpq-fields-section {
    margin-top: 2rem;
    padding: 1rem;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    background: #f9fafb;
  }

  .cpq-fields-section h3 {
    margin: 0 0 1rem 0;
    color: #374151;
  }

  .cpq-fields-section textarea,
  .cpq-fields-section input {
    width: 100%;
    margin-bottom: 0.5rem;
    padding: 0.5rem;
    border: 1px solid #d1d5db;
    border-radius: 4px;
  }

  .usps-section,
  .upsells-section {
    margin-top: 1rem;
  }

  .usps-section label,
  .upsells-section label {
    display: block;
    font-weight: 500;
    margin-bottom: 0.5rem;
    color: #374151;
  }

  .usp-item,
  .upsell-item {
    display: flex;
    gap: 0.5rem;
    margin-bottom: 0.5rem;
    align-items: center;
  }

  .usp-item input,
  .upsell-item input {
    flex: 1;
    margin-bottom: 0;
  }

  .usp-item button,
  .upsell-item button {
    padding: 0.25rem 0.5rem;
    background: #ef4444;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.875rem;
  }

  .required-badge,
  .quote-badge {
    padding: 0.125rem 0.375rem;
    border-radius: 9999px;
    font-size: 0.75rem;
    font-weight: 500;
  }

  .required-badge {
    background: #fef3c7;
    color: #92400e;
  }

  .quote-badge {
    background: #dbeafe;
    color: #1e40af;
  }

  .add-option-btn {
    padding: 0.5rem 1rem;
    background: #10b981;
    color: white;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    font-weight: 500;
  }

  .number-config-section,
  .choice-config-section {
    margin-top: 1rem;
    padding: 1rem;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    background: #f9fafb;
  }

  .display-settings {
    display: flex;
    gap: 1rem;
    margin-bottom: 1rem;
    flex-wrap: wrap;
  }

  .display-settings label {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    font-size: 0.875rem;
  }

  .choice-item {
    display: flex;
    gap: 0.5rem;
    margin-bottom: 0.5rem;
    align-items: center;
  }

  .choice-item input {
    flex: 1;
    padding: 0.375rem;
    border: 1px solid #d1d5db;
    border-radius: 4px;
  }

  .feed-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
  }

  .add-product-btn {
    padding: 0.75rem 1.5rem;
    background: #10b981;
    color: white;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    font-weight: 500;
    transition: background-color 0.2s;
  }

  .add-product-btn:hover {
    background: #059669;
  }
</style>
